import type { IBrowserClient } from '@splitsoftware/splitio/types/splitio';

import { FF, type FeatureFlag } from './featureFlags.generated';

/**
 * Console-friendly handle for inspecting the live Split client from the page,
 * mounted on `window.__SIRONA_FF__`. STRESS, E2E specs, and local-dev sessions
 * use it to read back the *settled* treatment for a flag — i.e. what the
 * underlying SplitClient actually returns after any
 * `__SIRONA_SPLIT_MOCKS__` overrides are applied — instead of having to trust
 * the inputs that were injected into localStorage.
 */
export type SironaFFDebugger = {
  /**
   * Returns the settled treatment for `name` from the wrapped Split client.
   * Reflects FF_SET / FF_MOCKS overrides because the wrapper around
   * `getTreatment` consults `__SIRONA_SPLIT_MOCKS__` first.
   */
  getTreatment: (name: string) => string;
  /**
   * Returns a `{name -> treatment}` snapshot. Pass an explicit list to scope
   * the snapshot; pass nothing to fetch every flag in the generated FF enum
   * (mirrors the format of the printed STRESS FF table).
   */
  getTreatments: (names?: readonly string[]) => Record<string, string>;
  /** Underlying SplitClient (already wrapped with the local-storage mock layer). */
  client: IBrowserClient;
};

const ALL_FF_NAMES: readonly FeatureFlag[] = Object.freeze(Object.values(FF));

/**
 * Mounts `window.__SIRONA_FF__` so any logged-in session can introspect the
 * settled feature-flag treatments at runtime. Always invoked by
 * `getClientWithState` — the global is present in all environments once a
 * user is authenticated. On non-production deployments (and when `E2E_TEST`
 * or `LOCAL_SPLITS` is set) the client is wrapped with the
 * `__SIRONA_SPLIT_MOCKS__` localStorage layer; on production it is the raw
 * Split client. Re-runs are idempotent: a fresh client (e.g. after logout /
 * login) replaces the previous handle.
 */
export const exposeFfDebugGlobal = (client: IBrowserClient): void => {
  if (typeof window === 'undefined') return;
  const handle: SironaFFDebugger = {
    getTreatment: (name) => client.getTreatment(name),
    // Loop over the patched `getTreatment` (singular) rather than calling the SDK's
    // `getTreatments` directly. `makeGetTreatmentMockable` patches only `getTreatment`; the
    // SDK's `getTreatments` calls its own internal implementation and bypasses the
    // `__SIRONA_SPLIT_MOCKS__` localStorage layer, so VERIFY_FF would see real Split values
    // instead of the injected mocks.
    getTreatments: (names) =>
      Object.fromEntries(
        [...(names?.length ? names : ALL_FF_NAMES)].map((name) => [name, client.getTreatment(name)])
      ),
    client,
  };
  window.__SIRONA_FF__ = handle;
};

/**
 * Removes the `window.__SIRONA_FF__` handle. Used by tests; available for
 * future logout / multi-client flows that want to clear stale references.
 */
export const clearFfDebugGlobal = (): void => {
  if (typeof window === 'undefined') return;
  delete window.__SIRONA_FF__;
};
